import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Where the error came from:
 * - `HTTP` — a failed backend call (ErrorInterceptor)
 * - `GLOBAL_RUNTIME` — an exception Angular's ErrorHandler caught
 * - `USER_MESSAGE` — a red error toast the user was actually shown (TrackedToastrService)
 * - `UNHANDLED_REJECTION` — a rejected promise nobody caught
 * - `WINDOW_ERROR` — a script error that never reached Angular (third-party/async code)
 * - `SECURITY` — a blocked/refused action worth an ops look (non-whitelisted wallet, …)
 */
export type TelegramErrorType =
  | 'GLOBAL_RUNTIME'
  | 'HTTP'
  | 'USER_MESSAGE'
  | 'UNHANDLED_REJECTION'
  | 'WINDOW_ERROR'
  | 'SECURITY';

/** Same ladder as the backend's `telegram-alert-helper.js`, so both feeds read alike. */
export type TelegramAlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface TelegramErrorReport {
  type: TelegramErrorType;
  message: string;
  route?: string;
  name?: string;
  stack?: string | null;
  url?: string;
  method?: string;
  status?: number;
  /** Overrides the severity derived from `type`/`status`. */
  severity?: TelegramAlertSeverity;
  /** Logical tag shown in the headline, e.g. 'Wallet whitelist'. Defaults per type. */
  service?: string;
  /** Structured details rendered as the 📌 Context table (tokenId, wallet, action…). */
  context?: Record<string, unknown>;
  /**
   * The wording the user was actually shown, when it differs from `message`.
   * Also suppresses the toast echo, so the same text is not alerted twice.
   */
  userMessage?: string;
}

interface StackFrame {
  /** Rendered "function · file:line" label. */
  text: string;
  /** Just "file:line", used for the Source row of the table. */
  location: string;
  /** Consecutive identical frames folded into one line. */
  repeats: number;
}

interface TelegramConfig {
  enabled: boolean;
  /** Error toasts are the noisiest source (every failed validation is one) — separately switchable. */
  trackToasts: boolean;
}

/**
 * Backend relay that holds the bot credentials and does the actual Telegram call. This app's
 * `API_BASE_URL_V3` stops at `/api/v3` (no trailing slash), so the path starts with one; the
 * relay is shared with the Marketplace and the dApp rather than duplicated per app.
 */
const RELAY_PATH = '/market-place/report-client-error';

// Telegram rejects sendMessage payloads over 4096 chars.
const TELEGRAM_TEXT_LIMIT = 4096;
const STACK_LIMIT = 1200;
// Past the first few frames a browser trace is bundler plumbing, not a lead.
const STACK_FRAME_LIMIT = 8;
// Frames from these bundles are Angular/RxJS/zone.js internals: they never point at the bug.
const FRAMEWORK_FRAME = /(?:^|\/)(?:vendor|polyfills|runtime|styles|scripts)\.js|node_modules|zone\.js/i;
// Identical emoji/labels to the backend helper so one channel reads as one system.
const SEVERITY_EMOJI: Record<TelegramAlertSeverity, string> = {
  LOW: 'ℹ️',
  MEDIUM: '⚠️',
  HIGH: '🔶',
  CRITICAL: '🔴'
};
const CONTEXT_VALUE_LIMIT = 200;
// The ops channel also carries the backend's, the Marketplace's and the dApp's alerts, so every
// message says which app it came from.
const ALERT_DOMAIN = 'Frontend · Admin';
// The same error usually fires in bursts (render loop, retry loop). Report the
// first one and stay quiet about identical repeats for a minute.
const DEDUPE_WINDOW_MS = 60_000;
const DEDUPE_MAX_ENTRIES = 200;
// One failure normally surfaces twice: the interceptor reports the API error, then the
// component shows a toast carrying the same backend text — and zone.js hands unhandled
// rejections to BOTH the ErrorHandler and window.onunhandledrejection. Identical text
// arriving from a second source this soon is that echo, not a second failure.
const ECHO_WINDOW_MS = 15_000;
// Hard ceiling so an error storm cannot flood the channel or hit Telegram's own limits.
const RATE_LIMIT_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX_MESSAGES = 20;
// API host prefix → environment name shown in the alert's Env field.
const API_HOST_ENV_NAMES: Record<string, string> = {
  devapi: 'development',
  stagingapi: 'staging',
  preapi: 'pre-production',
  api: 'production'
};
// 401/403 are the normal expiry/logout path (HttpErrorInterceptorService already handles them),
// so they are noise rather than defects.
const IGNORED_HTTP_STATUSES = new Set([401, 403]);

/**
 * Fire-and-forget alerting of frontend errors into a Telegram channel.
 *
 * The message is still built here — the headline, the tables, the stack block below are what the
 * ops channel shows. Only the delivery moved: instead of calling the Telegram Bot API directly, the
 * rendered text is posted to the backend relay, which holds the bot token and the chat id. They
 * used to be injected into this bundle at build time, which meant the credentials were readable
 * by anyone who opened the Network tab.
 *
 * Deliberately uses native `fetch`, not HttpClient: an HttpClient call would pass
 * back through TelegramErrorInterceptor, so a failing relay request would report itself
 * in an endless loop. `report()` drops anything raised about the relay itself for the same reason.
 */
@Injectable({
  providedIn: 'root'
})
export class TelegramErrorReporterService {
  private readonly config: TelegramConfig = this.readConfig();
  private readonly relayUrl = `${environment.API_BASE_URL_V3}${RELAY_PATH}`;
  private readonly lastSentByFingerprint = new Map<string, number>();
  /** Normalised message text → when it was last reported, by any source. */
  private readonly lastSentByMessage = new Map<string, number>();
  private sentTimestamps: number[] = [];
  private listenersInstalled = false;

  constructor(private ngZone: NgZone) {}

  /**
   * Whether alerting is switched on for this build.
   * @returns {boolean} true when the feature flag is on; whether an alert actually reaches
   *   Telegram is decided by the backend relay, which owns the credentials
   */
  get isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Catch the errors that never reach Angular: promise rejections raised outside the zone
   * and script errors from third-party code. Called once at bootstrap (APP_INITIALIZER).
   * @returns {void}
   */
  installGlobalListeners(): void {
    if (this.listenersInstalled || !this.config.enabled || typeof window === 'undefined') {
      return;
    }
    this.listenersInstalled = true;

    // Outside the zone: an alert must not schedule change detection of its own.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason: unknown = event.reason;
        this.report({
          type: 'UNHANDLED_REJECTION',
          message: this.describeUnknown(reason),
          name: this.readErrorField(reason, 'name') || undefined,
          stack: this.readErrorField(reason, 'stack') || null
        });
      });

      window.addEventListener('error', (event: ErrorEvent) => {
        // Resource failures (a 404 image, a blocked script tag) also fire this event with no
        // `error` object. They are not application faults and would swamp the channel.
        if (!event.error) {
          return;
        }
        this.report({
          type: 'WINDOW_ERROR',
          message: this.describeUnknown(event.error) || event.message,
          name: this.readErrorField(event.error, 'name') || undefined,
          stack: this.readErrorField(event.error, 'stack') || null
        });
      });
    });
  }

  /**
   * Sends one error to the alert channel, unless it is ignored, a repeat, or over the rate limit.
   * Never throws.
   * @param {TelegramErrorReport} report what failed and where
   * @returns {void}
   */
  report(report: TelegramErrorReport): void {
    if (!this.config.enabled) {
      return;
    }

    // A failure of the relay itself must never be reported through the relay.
    if (report.url?.startsWith(this.relayUrl)) {
      return;
    }

    if (report.type === 'HTTP' && report.status !== undefined && IGNORED_HTTP_STATUSES.has(report.status)) {
      return;
    }

    if (report.type === 'USER_MESSAGE' && !this.config.trackToasts) {
      return;
    }

    if (this.isDuplicate(report) || this.isRateLimited()) {
      return;
    }

    this.send(this.buildText(report));
  }

  private describeUnknown(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value instanceof Error) {
      return value.message;
    }
    const message = this.readErrorField(value, 'message');
    if (message.trim()) {
      return message;
    }
    try {
      return JSON.stringify(value) ?? String(value);
    } catch {
      return String(value);
    }
  }

  // Errors from wallets and viem are often plain objects, not Error instances.
  private readErrorField(value: unknown, field: 'name' | 'message' | 'stack'): string {
    if (!value || typeof value !== 'object') {
      return '';
    }
    const fieldValue = (value as Record<string, unknown>)[field];
    return typeof fieldValue === 'string' ? fieldValue : '';
  }

  private send(text: string): void {
    // Outside the Angular zone: alerting must not trigger change detection or
    // keep the app from reaching zone stability.
    this.ngZone.runOutsideAngular(() => {
      fetch(this.relayUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        keepalive: true
      }).catch(() => {
        // Never rethrow — a failed alert must not become an app error. There is no
        // logger in this app and console output is not wanted in production, so a
        // failed or rejected (e.g. HTTP 400 on over-long text) alert is dropped.
      });
    });
  }

  /**
   * Renders the report as a headline plus a monospace key/value table, so an alert
   * can be read at a glance on a phone instead of parsed line by line.
   */
  private buildText(report: TelegramErrorReport): string {
    const frames = report.stack ? this.appFrames(report.stack) : [];
    const blocks: string[] = [
      this.buildHeadline(report),
      this.buildTable(report, frames[0]),
      this.buildMessage(report)
    ];

    if (report.userMessage) {
      blocks.push(`<b>🙋 What the user saw</b>\n${this.escape(this.redact(this.singleLine(report.userMessage)))}`);
    }

    const context = this.buildContext(report.context);
    if (context) {
      blocks.push(context);
    }

    const stack = this.buildStack(frames);
    if (stack) {
      blocks.push(stack);
    }

    return blocks.join('\n\n').slice(0, TELEGRAM_TEXT_LIMIT);
  }

  /**
   * Two lines, matching the backend alert helper: severity + what it is, then where and when.
   */
  private buildHeadline(report: TelegramErrorReport): string {
    const severity = this.severityOf(report);
    const label = report.service ?? this.categoryLabel(report);

    return (
      `${SEVERITY_EMOJI[severity]} <b>${severity}</b> — <b>${this.escape(label)}</b>\n` +
      `🖥 Domain: <code>${ALERT_DOMAIN}</code>   🌐 Env: <code>${this.escape(this.environmentName())}</code>\n` +
      `🕒 <code>${this.escape(this.readableTime(new Date()))}</code>`
    );
  }

  private severityOf(report: TelegramErrorReport): TelegramAlertSeverity {
    if (report.severity) {
      return report.severity;
    }

    switch (report.type) {
      // A toast is a handled outcome the user was told about, not a fault on its own.
      case 'USER_MESSAGE':
        return 'LOW';
      case 'SECURITY':
        return 'HIGH';
      case 'HTTP':
        return report.status === undefined || report.status === 0 || report.status >= 500 ? 'HIGH' : 'MEDIUM';
      default:
        return 'HIGH';
    }
  }

  private categoryLabel(report: TelegramErrorReport): string {
    switch (report.type) {
      case 'SECURITY':
        return 'Security';
      case 'USER_MESSAGE':
        // Distinct on purpose: this is the text a real user was shown, not an internal throw.
        return 'Error shown to user';
      case 'UNHANDLED_REJECTION':
        return 'Unhandled promise rejection';
      case 'HTTP':
        return report.status === undefined || report.status === 0
          ? 'Network request failed'
          : `API ${report.status} · ${this.statusText(report.status)}`;
      default:
        return 'App error';
    }
  }

  private buildTable(report: TelegramErrorReport, topFrame?: StackFrame): string {
    const rows: Array<[string, string]> = [];

    if (report.status !== undefined) {
      rows.push(['Status', `${report.status} ${this.statusText(report.status)}`]);
    }

    if (report.url) {
      const target = this.splitUrl(report.url);
      rows.push(['Endpoint', `${report.method ? `${report.method} ` : ''}${target.path}`]);
      if (target.host) {
        rows.push(['Server', target.host]);
      }
    } else if (report.method) {
      rows.push(['Method', report.method]);
    }

    rows.push(['Page', report.route ?? this.currentRoute()]);

    if (report.name) {
      rows.push(['Error', report.name]);
    }

    if (topFrame) {
      rows.push(['Source', topFrame.location]);
    }

    return this.table(rows);
  }

  /**
   * Structured details as a padded table rather than raw JSON: same shape as the
   * backend's 📌 Context block, minus the braces and quotes nobody needs to read.
   */
  private buildContext(context?: Record<string, unknown>): string {
    if (!context) {
      return '';
    }

    const rows = Object.entries(context)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]): [string, string] => [this.humanKey(key), this.contextValue(value)]);

    return rows.length ? `<b>📌 Context</b>\n${this.table(rows)}` : '';
  }

  private table(rows: Array<[string, string]>): string {
    const labelWidth = Math.max(...rows.map(([label]) => label.length));
    const body = rows.map(([label, value]) => `${label.padEnd(labelWidth)}  ${this.singleLine(value)}`).join('\n');

    return `<pre>${this.escape(body)}</pre>`;
  }

  // "walletAddress" / "wallet_address" → "Wallet address".
  private humanKey(key: string): string {
    const words = key.replace(/[_-]+/g, ' ').replace(/([a-z\d])([A-Z])/g, '$1 $2').toLowerCase().trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  private contextValue(value: unknown): string {
    const text =
      typeof value === 'object'
        ? (() => {
            try {
              return JSON.stringify(value) ?? String(value);
            } catch {
              return String(value);
            }
          })()
        : String(value);

    // Wallet addresses stay whole here: a security alert is useless without the address,
    // and it is public on-chain data anyway. Tokens and emails are still removed.
    return this.redact(text, { keepAddresses: true }).slice(0, CONTEXT_VALUE_LIMIT);
  }

  private buildMessage(report: TelegramErrorReport): string {
    // A security alert names the wallet it is about — shortening it there would be
    // shortening the only fact that matters. Elsewhere addresses stay abbreviated.
    const message = this.redact(this.singleLine(report.message), { keepAddresses: report.type === 'SECURITY' });

    return `<b>💬 What happened</b>\n${this.escape(message)}`;
  }

  /**
   * A raw browser stack is mostly framework plumbing wrapped in absolute bundle URLs —
   * unreadable on a phone. Keep the frames that point at our own code, drop the origin
   * and column numbers, and fold the repeated RxJS hops into a single counted line.
   */
  private appFrames(stack: string): StackFrame[] {
    const frames: StackFrame[] = [];

    for (const line of this.redact(stack).split('\n')) {
      const frame = this.parseFrame(line.trim());
      if (!frame) {
        continue;
      }

      const previous = frames[frames.length - 1];
      if (previous && previous.text === frame.text) {
        previous.repeats++;
        continue;
      }

      frames.push(frame);
    }

    return frames;
  }

  private parseFrame(line: string): StackFrame | undefined {
    // "at Object.next (http://host/chunk.js:16700:69)" or "at http://host/chunk.js:75863:18"
    const match = /^at\s+(?:(.+?)\s+)?\(?((?:[a-z]+:\/\/|\/|webpack).*?)\)?$/i.exec(line);
    if (!match) {
      return undefined;
    }

    const location = match[2]
      .replace(/^[a-z]+:\/\/[^/]+\//i, '')
      .replace(/^webpack:\/{0,3}/i, '')
      .replace(/:(\d+):\d+$/, ':$1');

    if (FRAMEWORK_FRAME.test(location)) {
      return undefined;
    }

    const fn = (match[1] ?? '').replace(/^(?:new|async)\s+/, '');
    return { text: fn ? `${fn} · ${location}` : location, location, repeats: 1 };
  }

  private buildStack(frames: StackFrame[]): string {
    // A single frame is already shown as the Source row — no need to repeat it.
    if (frames.length < 2) {
      return '';
    }

    const shown = frames.slice(0, STACK_FRAME_LIMIT);
    const lines = shown.map((frame, index) => {
      const repeats = frame.repeats > 1 ? ` (×${frame.repeats})` : '';
      return `${index + 1}. ${frame.text}${repeats}`;
    });

    const hidden = frames.length - shown.length;
    if (hidden > 0) {
      lines.push(`… ${hidden} more frame${hidden === 1 ? '' : 's'}`);
    }

    // Expandable so the trace stays one collapsed line until someone taps it.
    return `<b>🧵 Where it broke</b>\n<blockquote expandable>${this.escape(
      lines.join('\n').slice(0, STACK_LIMIT)
    )}</blockquote>`;
  }

  // Values live inside a padded table, so a stray newline or tab would break the columns.
  private singleLine(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
  }

  private readableTime(date: Date): string {
    const iso = date.toISOString();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[Number(iso.slice(5, 7)) - 1];
    return `${iso.slice(8, 10)} ${month} ${iso.slice(0, 4)}, ${iso.slice(11, 19)} UTC`;
  }

  // Plain-English label so a reader does not have to remember what the number means.
  private statusText(status: number): string {
    const texts: Record<number, string> = {
      0: 'No response',
      400: 'Bad request',
      404: 'Not found',
      408: 'Request timeout',
      409: 'Conflict',
      413: 'Payload too large',
      422: 'Validation failed',
      429: 'Too many requests',
      500: 'Server error',
      502: 'Bad gateway',
      503: 'Service unavailable',
      504: 'Gateway timeout'
    };

    return texts[status] ?? (status >= 500 ? 'Server error' : status >= 400 ? 'Request failed' : 'Unexpected response');
  }

  // The host adds nothing to the table when the request is already relative.
  private splitUrl(url: string): { host: string; path: string } {
    const stripped = this.stripQuery(url);
    const match = /^[a-z]+:\/\/([^/]+)(\/.*)?$/i.exec(stripped);

    return match ? { host: match[1], path: match[2] || '/' } : { host: '', path: stripped };
  }

  private isDuplicate(report: TelegramErrorReport): boolean {
    const now = Date.now();
    const fingerprint = [
      report.type,
      report.name ?? '',
      report.status ?? '',
      this.stripQuery(report.url ?? ''),
      report.message.slice(0, 160)
    ].join('|');

    const lastSentAt = this.lastSentByFingerprint.get(fingerprint);
    if (lastSentAt !== undefined && now - lastSentAt < DEDUPE_WINDOW_MS) {
      return true;
    }

    // Cross-source echo: same text, different origin, moments apart.
    const messageKey = this.messageKey(report.message);
    const lastMessageAt = this.lastSentByMessage.get(messageKey);
    if (lastMessageAt !== undefined && now - lastMessageAt < ECHO_WINDOW_MS) {
      return true;
    }

    if (this.lastSentByFingerprint.size >= DEDUPE_MAX_ENTRIES) {
      this.lastSentByFingerprint.clear();
    }
    if (this.lastSentByMessage.size >= DEDUPE_MAX_ENTRIES) {
      this.lastSentByMessage.clear();
    }

    this.lastSentByFingerprint.set(fingerprint, now);
    this.lastSentByMessage.set(messageKey, now);
    // The toast carrying this wording is about to fire; it is already quoted in this
    // alert, so register it as sent rather than letting it arrive as a second one.
    if (report.userMessage) {
      this.lastSentByMessage.set(this.messageKey(report.userMessage), now);
    }
    return false;
  }

  /** Comparison key for the echo check — case and whitespace differences are not new errors. */
  private messageKey(message: string): string {
    return this.singleLine(message).toLowerCase().slice(0, 160);
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    this.sentTimestamps = this.sentTimestamps.filter((timestamp) => now - timestamp < RATE_LIMIT_WINDOW_MS);

    if (this.sentTimestamps.length >= RATE_LIMIT_MAX_MESSAGES) {
      return true;
    }

    this.sentTimestamps.push(now);
    return false;
  }

  // Both flags are optional in AppEnvironment. No credentials are read: the bot token and
  // chat id live in the backend's env.
  private readConfig(): TelegramConfig {
    return {
      enabled: environment.ENABLE_TELEGRAM_ERROR_ALERTS === true,
      // Opt-out rather than opt-in: absent key means toasts are tracked.
      trackToasts: environment.TELEGRAM_TRACK_TOAST_ERRORS !== false
    };
  }

  /**
   * The admin env files carry no `envName`, so the environment is read from the API host the
   * build talks to — it is unique per environment and already in every env file.
   */
  private environmentName(): string {
    try {
      if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
        return 'local';
      }
      const host = new URL(environment.API_BASE_URL_V3).hostname;
      return API_HOST_ENV_NAMES[host.split('.')[0]] ?? host;
    } catch {
      return 'UNKNOWN';
    }
  }

  // Path routing: the route is the pathname.
  private currentRoute(): string {
    try {
      return this.stripQuery(window.location.pathname + window.location.search);
    } catch {
      return '/';
    }
  }

  // Query strings carry tokens and emails on the auth/verification routes.
  private stripQuery(url: string): string {
    const cutIndex = url.indexOf('?');
    return cutIndex === -1 ? url : `${url.slice(0, cutIndex)}?[REDACTED]`;
  }

  // Tokens, emails and addresses are embedded in prose here, so they are matched anywhere in the text.
  private redact(value: string, options: { keepAddresses?: boolean } = {}): string {
    const redacted = value
      .replace(/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g, '[REDACTED_TOKEN]')
      .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [REDACTED]')
      .replace(/[^\s@]+@[^\s@]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]');

    return options.keepAddresses
      ? redacted
      : redacted.replace(/0x[a-fA-F0-9]{40,}/g, (match) => `${match.slice(0, 6)}...${match.slice(-4)}`);
  }

  private escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
